import { AccountReceivable, Organization, PortfolioResult, ScoreLogic } from '@/api/';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { EllipsisVerticalIcon } from '@heroicons/react/24/outline';
import { useStore } from '@nanostores/react';
import { Badge, Loader2, Play, CheckCircle, XCircle } from 'lucide-react';
import { DeleteScoreLogicConfirmation } from './DeleteScoreLogicConfirmation';
import { cn } from '@/lib/utils';
/**
 * We'll update the selected project in the store and route
 * to the project page.
 */
function handleSelectScoreLogic(
  organization: Organization,
  scorelogic: ScoreLogic,
) {
  $scorelogic.set(scorelogic);

  openPage($router, 'score_logic', {
    organizationId: organization.id,
    scorelogicId: scorelogic.id,
  });
}

export const ScoreLogicListing = () => {
  const org = useStore($org);

  const scorelogics = useStore($scorelogics);
  const loadingScoreLogics = useStore($loadingScoreLogics);

  const sortedScoreLogics = scorelogics
    ?.slice()
    ?.sort(
      (a, b) =>
        new Date(b.createdAtUtc).getTime() - new Date(a.createdAtUtc).getTime(),
    );

  const [isDeleteScoreLogicModalOpen, setIsDeleteScoreLogicModalOpen] =
    useState(false);
  const [scoreLogicToBeDeleted, setScoreLogicToBeDeleted] = useState('');

  const selectedScoreLogic = useStore($scorelogic);
  
  // Handle empty state when no account receivables exist
  if (!loadingScoreLogics && (!scorelogics || scorelogics.length === 0)) {
    return (
      <div className="flex h-32 items-center justify-center">
        <p>No score logics found.</p>
      </div>
    );
  }

  const formatDateString = (date: string | Date) => {
    const d = new Date(date);
    const month = String(d.getMonth() + 1).padStart(2, '0'); // Months are zero-based, so add 1
    const day = String(d.getDate()).padStart(2, '0');
    const year = d.getFullYear();
    return `${month}-${day}-${year}`;
  };


  function getDateRangeString(
    startDate: string | Date,
    endDate: string | Date,
  ) {
    const startDateString = formatDateString(startDate);

    const endDateString = formatDateString(endDate);

    if (startDateString === endDateString) {
      return `for ${startDateString}`;
    } else {
      return `between ${startDateString} and ${endDateString}`;
    }
  }


  return (
    <>
      {loadingScoreLogics ? (
        <div className="flex h-32 items-center justify-center">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      ) : (
        <ul role="list" className="divide-y divide-border">
          {sortedScoreLogics?.map(scorelogic => {
            return (
              <li
                key={scorelogic.id}
                className="flex items-center justify-between gap-x-6 py-5"
              >

                <div className="min-w-0">
                  <div className="flex items-center gap-x-3">
                    <p className="text-sm font-semibold leading-6">
                      {scorelogic.name}
                    </p>
                    {/* I hacked this in here because I couldn't get the badge to work, so i used cursor to make it a span */}
                    <span
                      className={cn(
                        "inline-flex items-center rounded-md px-2 py-1",
                        scorelogic.isActive 
                          ? "bg-green-600 text-white" 
                          : "bg-red-600 text-white",
                        "text-sm font-medium ring-1 ring-inset"
                      )}
                    >
                      {scorelogic.isActive ? (
                        <CheckCircle className="h-4 w-4 mr-1" />
                      ) : (
                        <XCircle className="h-4 w-4 mr-1" />
                      )}
                      {scorelogic.isActive ? 'Active' : 'Inactive'}
                    </span>
                  </div>

                  <div className="mt-1 flex items-center gap-x-2 text-xs leading-5 text-muted-foreground">
                    <p className="whitespace-nowrap">
                      Created on{' '}
                      <time dateTime={scorelogic.createdAtUtc}>
                        {new Date(scorelogic.createdAtUtc).toLocaleDateString()}
                      </time>
                    </p>
                    <svg viewBox="0 0 2 2" className="h-0.5 w-0.5 fill-current">
                      <circle cx={1} cy={1} r={1} />
                    </svg>
                    <p className="truncate">
                      Created by {scorelogic.createdBy?.email || 'Unknown'}
                    </p>
                  </div>

                  <div className="mt-1 flex items-center gap-x-2 text-sm leading-5 text-muted-foreground">


                    <div className="flex items-start">
                      <div>
                        <div className="flex items-center justify-start">
                          <svg
                            viewBox="0 0 2 2"
                            className="h-0.5 w-0.5 fill-current"
                          >
                            <circle cx={1} cy={1} r={1} />
                          </svg>
                          <p className="ml-2 truncate">
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="flex flex-none items-center gap-x-4">
                  <>
                    <Button
                      onClick={e => {
                        if (org ) handleSelectScoreLogic(org,scorelogic);
                      }}
                    >
                      View Score Logic
                      <span className="sr-only">, {scorelogic.name}</span>
                    </Button>
                  </>

                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="outline" size="icon">
                        <EllipsisVerticalIcon className="h-4 w-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent className="w-56">
                      <DropdownMenuLabel>Options</DropdownMenuLabel>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem
                        onClick={() => {
                          setScoreLogicToBeDeleted(scorelogic.id);
                          setIsDeleteScoreLogicModalOpen(true);
                        }}
                      >
                        Delete Score Logic
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              </li>
            );
          })}
        </ul>
      )}

      <DeleteScoreLogicConfirmation
        isOpen={isDeleteScoreLogicModalOpen}
        handleOpen={setIsDeleteScoreLogicModalOpen}
        scorelogicId={scoreLogicToBeDeleted}
      />
    </>
  );
};
